import { useState, useEffect, useMemo } from 'react';
import AppLayout from '../layout/AppLayout';
import { Spinner } from 'react-bootstrap';
import regenerateToken from '../regenerateToken';
import Loader from '../loader/loader';
import axios from 'axios';
import Cookies from 'js-cookie';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';
import {
  FaGlobe, FaPalette, FaLink, FaCheckCircle, FaCog,
  FaChartBar, FaBookOpen, FaUserMd, FaCrop, FaUserInjured,
  FaShieldAlt, FaFilePdf, FaClipboardList, FaCloudUploadAlt,
  FaQrcode, FaSlidersH, FaSearch, FaTimes,
  FaPlus, FaTrash, FaInfoCircle, FaExclamationTriangle, FaExternalLinkAlt,
  FaBaby,
} from 'react-icons/fa';
import './reportConfiguration.css';
import { useAccessConfig } from '../layout/AccessContext';

/* ------------------------------------------------------------------ */
/*  HIDDEN_FIELDS – fields intentionally excluded from the UI.        */
/*  To restore any field later, simply remove its key from this set.  */
/* ------------------------------------------------------------------ */
const HIDDEN_FIELDS = new Set([
  'gender',
  'age',
  'removeBodySummaryLimit',
  'showHistoricalForStratifiedRanges',
  'generateCoverInnerPage',
  'coverInnerPagePath',
  'signUrl',
  'barcodeNumber',
  'customDisclaimerImageBase64',
  'showFollowUpText',
  'showCombinedFollowUps',
  'isVizAppProcessor',
  'test',
  'IsMedibuddy',
  'pullReportConfig',
  'pullClientData',
  'useBackgroundImage',
  'brandLogoBase64',
]);

/* ------------------------------------------------------------------ */
/*  SECTION_CONFIG – single source of truth for all fields & sections */
/*  Order: Identity → Appearance → Content → Layout → Output → Assets */
/* ------------------------------------------------------------------ */
const SECTION_CONFIG = [
  /* 1 ── General & Identity ────────────────────────────────────── */
  {
    id: 'general',
    icon: FaGlobe,
    title: 'General & Identity',
    desc: 'Core report type, language preferences and typography settings.',
    grid: 3,
    fields: [
      { key: 'clientId', label: 'Client ID', type: 'text', disabled: true },
      { key: 'center', label: 'Center', type: 'text', disabled: true },
      {
        key: 'reportType', label: 'Report Type', type: 'select', disabled: false, options: [
          { value: 'dynamic', label: 'Personal' }, { value: 'advanced', label: 'Total' }, { value: 'compact', label: 'Compact' },
        ]
      },
      {
        key: 'curLang', label: 'Report Language', type: 'select', options: [
          { value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' }, { value: 'ar', label: 'Arabic' },
        ]
      },
      {
        key: 'fallbackLang', label: 'Fallback Language', type: 'select', options: [
          { value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' }, { value: 'ar', label: 'Arabic' },
        ]
      },
      {
        key: 'direction', label: 'Text Direction', type: 'select', options: [
          { value: 'ltr', label: 'LTR' }, { value: 'rtl', label: 'RTL' },
        ]
      },
      { key: 'fontFamily', label: 'Font Family', type: 'text' },
      { key: 'fontURL', label: 'Font URL', type: 'text' },
      { key: 'fontSize', label: 'Font Size', type: 'text' },
    ],
  },

  /* 2 ── Color & Styling ───────────────────────────────────────── */
  {
    id: 'colorStyling',
    icon: FaPalette,
    title: 'Color & Styling',
    desc: 'Define branding colors and visual display preferences for your reports.',
    grid: 3,
    fields: [
      { key: 'headingColor', label: 'Heading Color', type: 'color' },
      { key: 'cardBackgroundColorColored', label: 'Card BG (Colored)', type: 'color' },
      { key: 'cardBackgroundColorGrayscaled', label: 'Card BG (Grayscale)', type: 'color' },
      { key: 'showcoloredValues', label: 'Show Colored Values', type: 'toggle' },
      { key: 'showBoldAbnormal', label: 'Bold Abnormal Values', type: 'toggle' },
      { key: 'showcardBackgroundColor', label: 'Show Card Background', type: 'toggle' },
      { key: 'useClientColors', label: 'Use Client Colors', type: 'toggle' },
      { key: 'useBorderlineConfig', label: 'Borderline Config', type: 'toggle' },
      { key: 'showMinMaxDisplayRange', label: 'Min/Max Display Range', type: 'toggle' },
    ],
  },

  /* 3 ── Report Summary ────────────────────────────────────────── */
  {
    id: 'reportSummary',
    icon: FaChartBar,
    title: 'Report Summary',
    desc: 'Control the top-level summary, abnormal highlighting and risk score display.',
    grid: 3,
    fields: [
      { key: 'showSummary', label: 'Show Summary', type: 'toggle' },
      { key: 'showSummaryEnd', label: 'Summary at End', type: 'toggle' },
      { key: 'showOnlyAbnormalSummary', label: 'Only Abnormal Summary', type: 'toggle' },
      { key: 'summaryType', label: 'Summary Type', type: 'number' },
      { key: 'showAbnormalFirst', label: 'Abnormal First', type: 'toggle' },
      { key: 'showRiskScore', label: 'Show Risk Score', type: 'toggle' },
    ],
  },

  /* 4 ── Body Summary & Scale ──────────────────────────────────── */
  {
    id: 'bodySummary',
    icon: FaSlidersH,
    title: 'Body Summary & Scale',
    desc: 'Configure body summary type, dots style, package display, barcodes and scaling.',
    grid: 3,
    fields: [
      { key: 'showbodySummary', label: 'Show Body Summary', type: 'toggle' },
      { key: 'bodySummaryType', label: 'Body Summary Type', type: 'number' },
      { key: 'summaryDotsType', label: 'Summary Dots Type', type: 'number' },
      { key: 'showBodySummaryPackagename', label: 'Package Name in Body', type: 'toggle' },
      { key: 'showBarCodeInBodySummary', label: 'Barcode in Body Summary', type: 'toggle' },
      { key: 'showBarCodeInReportSummary', label: 'Barcode in Report Summary', type: 'toggle' },
      { key: 'showNoteInBodySummary', label: 'Note in Body Summary', type: 'toggle' },
      { key: 'useCustomBodySummary', label: 'Custom Body Summary', type: 'toggle' },
      { key: 'maxBodyHeight', label: 'Max Body Height', type: 'number' },
      { key: 'bodyScale', label: 'Body Scale', type: 'number' },
    ],
  },

  /* 5 ── QR Code & Historical Data ─────────────────────────────── */
  {
    id: 'qrHistory',
    icon: FaQrcode,
    title: 'QR Code & Historical Data',
    desc: 'Manage QR code placement, URL and historical trend settings.',
    grid: 3,
    fields: [
      { key: 'showQrEnd', label: 'Show QR at End', type: 'toggle' },
      {
        key: 'qrCodePosition', label: 'QR Position', type: 'select', options: [
          { value: 'end', label: 'End' }, { value: 'start', label: 'Start' },
        ]
      },
      { key: 'qrUrl', label: 'QR URL', type: 'text' },
      { key: 'historical', label: 'Historical', type: 'toggle' },
      { key: 'historicalType', label: 'Historical Type', type: 'number' },
    ],
  },

  /* 6 ── Cover & Extra Pages ───────────────────────────────────── */
  {
    id: 'coverPages',
    icon: FaBookOpen,
    title: 'Cover & Extra Pages',
    desc: 'Configure cover page generation, back page, intro and additional test pages.',
    grid: 3,
    fields: [
      { key: 'generateCovePage', label: 'Generate Cover Page', type: 'toggle' },
      { key: 'dynamicCoverPage', label: 'Dynamic Cover Page', type: 'toggle' },
      { key: 'generateCoverPagePrint', label: 'Cover Page for Print', type: 'toggle' },
      { key: 'showBackPage', label: 'Show Back Page', type: 'toggle' },
      { key: 'backPagePath', label: 'Back Page Path', type: 'text' },
      { key: 'showIntroPage', label: 'Show Intro Page', type: 'toggle' },
      { key: 'showPatientIntro', label: 'Show Patient Intro', type: 'toggle' },
      { key: 'showAllOtherTests', label: 'Show All Other Tests', type: 'toggle' },
    ],
  },

  /* 7 ── Prenatal ──────────────────────────────────────────────── */
  {
    id: 'prenatal',
    icon: FaBaby,
    title: 'Prenatal',
    desc: 'Configure prenatal report cover/back pages, headers, footers and margins.',
    grid: 3,
    fields: [
      { key: 'prenatal', label: 'Prenatal', type: 'toggle' },
      { key: 'generatePrenatalCoverPage', label: 'Generate Prenatal Cover Page', type: 'toggle' },
      { key: 'prenatalCoverPageImage', label: 'Prenatal Cover Page Image', type: 'text' },
      { key: 'generatePrenatalBackPage', label: 'Generate Prenatal Back Page', type: 'toggle' },
      { key: 'prenatalBackPageImage', label: 'Prenatal Back Page Image', type: 'text' },
      { key: 'prenatalHeaderBase64', label: 'Prenatal Header (Base64)', type: 'text' },
      { key: 'prenatalFooterBase64', label: 'Prenatal Footer (Base64)', type: 'text' },
      { key: 'prenatalHeaderHeight', label: 'Prenatal Header Height', type: 'text' },
      { key: 'prenatalFooterHeight', label: 'Prenatal Footer Height', type: 'text' },
      { key: 'prenatalHeaderMargin', label: 'Prenatal Header Margin', type: 'text' },
      { key: 'prenatalFooterMargin', label: 'Prenatal Footer Margin', type: 'text' },
      { key: 'showPoweredByLogo', label: 'Show Powered-By Logo', type: 'toggle' },
    ],
  },

  /* 8 ── Header, Footer & Margins ──────────────────────────────── */
  {
    id: 'headerFooter',
    icon: FaCrop,
    title: 'Header, Footer & Margins',
    desc: 'Manage header and footer templates, page margins and positioning.',
    grid: 3,
    fields: [
      { key: 'headerTemplateType', label: 'Header Template', type: 'text' },
      { key: 'footerTemplateType', label: 'Footer Template', type: 'text' },
      { key: 'typePatientDetailsHeader', label: 'Patient Details Header', type: 'text' },
      { key: 'headerPrefix', label: 'Header Prefix', type: 'text' },
      { key: 'footerPrefix', label: 'Footer Prefix', type: 'text' },
      { key: 'backgroundHeaderMargin', label: 'Header Margin', type: 'number' },
      { key: 'backgroundFooterMargin', label: 'Footer Margin', type: 'number' },
      { key: 'pageNumberMargin', label: 'Page Number Margin', type: 'text' },
      { key: 'pageleftMargin', label: 'Page Left Margin', type: 'text' },
      { key: 'feedbackButtonPosition', label: 'Feedback Btn Position', type: 'number' },
      { key: 'hideFooter', label: 'Hide Footer', type: 'toggle' },
    ],
  },

  /* 8 ── Patient Details & Display ─────────────────────────────── */
  {
    id: 'patientDisplay',
    icon: FaUserInjured,
    title: 'Patient Details & Display',
    desc: 'Configure patient info display, barcodes, booking links, disclaimers and terms.',
    grid: 3,
    fields: [
      { key: 'patientDetailsType', label: 'Patient Details Type', type: 'number' },
      { key: 'showPackageName', label: 'Show Package Name', type: 'toggle' },
      { key: 'showJsonNames', label: 'Show JSON Names', type: 'toggle' },
      { key: 'showBarCode', label: 'Show Barcode', type: 'toggle' },
      { key: 'showBookNowButton', label: 'Show Book Now', type: 'toggle' },
      { key: 'bookNowUrl', label: 'Book Now URL', type: 'text' },
      { key: 'showDisclaimer', label: 'Show Disclaimer', type: 'toggle' },
      { key: 'showCustomDisclaimer', label: 'Custom Disclaimer', type: 'toggle' },
      { key: 'followUptext', label: 'Follow-Up Text', type: 'text' },
      { key: 'showTermsAndConditions', label: 'Terms & Conditions', type: 'toggle' },
      { key: 'showComments', label: 'Show Comments', type: 'toggle' },
    ],
  },

  /* 9 ── Doctor Signatures & Remarks ───────────────────────────── */
  {
    id: 'doctorSign',
    icon: FaUserMd,
    title: 'Doctor Signatures & Remarks',
    desc: 'Control doctor signature placement, alignment, dimensions and remark visibility.',
    grid: 3,
    fields: [
      { key: 'showDocSign', label: 'Show Doctor Signature', type: 'toggle' },
      { key: 'showDoctorRemarks', label: 'Show Doctor Remarks', type: 'toggle' },
      { key: 'showDoctorSignaturesInSummary', label: 'Signatures in Summary', type: 'toggle' },
      { key: 'showDoctorSignatureAtEnd', label: 'Signature at End', type: 'toggle' },
      { key: 'showDoctorSignAtSummaryEnd', label: 'Sign at Summary End', type: 'toggle' },
      {
        key: 'doctorSignAlignment', label: 'Sign Alignment', type: 'select', options: [
          { value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' },
        ]
      },
      { key: 'docSignHeight', label: 'Sign Height (px)', type: 'number' },
    ],
  },

  /* 10 ── Accreditation & Logos ─────────────────────────────────── */
  {
    id: 'accredLogos',
    icon: FaShieldAlt,
    title: 'Accreditation & Logos',
    desc: 'Manage accreditation badges, powered-by logos and NABL/CAP/NGSP images.',
    grid: 3,
    fields: [
      { key: 'showAccrediation', label: 'Show Accreditation', type: 'toggle' },
      { key: 'showAccredation', label: 'Show Accredation', type: 'toggle' },
      { key: 'showParameterAccreditation', label: 'Parameter Accreditation', type: 'toggle' },
      { key: 'showProfileAccredition', label: 'Profile Accreditation', type: 'toggle' },
      { key: 'showAccreditionInAllPages', label: 'Accreditation All Pages', type: 'toggle' },
      { key: 'showAccreditionInReportSummary', label: 'In Report Summary', type: 'toggle' },
      { key: 'showAccreditionInBodySummary', label: 'In Body Summary', type: 'toggle' },
      { key: 'hidePoweredByNiroggyanLogo', label: 'Hide Powered-By Logo', type: 'toggle' },
      { key: 'hideNiroggyanLogoInRecommendation', label: 'Hide Logo in Recommendations', type: 'toggle' },
      {
        key: 'poweredByLogoPosition', label: 'Logo Position', type: 'select', options: [
          { value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' },
        ]
      },
      { key: 'nablImg', label: 'NABL Image URL', type: 'text' },
      { key: 'capImg', label: 'CAP Image URL', type: 'text' },
      { key: 'ngspImg', label: 'NGSP Image URL', type: 'text' },
    ],
  },

  /* 11 ── Recommendations, Tips & Feedback ─────────────────────── */
  {
    id: 'recoFeedback',
    icon: FaClipboardList,
    title: 'Recommendations, Tips & Feedback',
    desc: 'Toggle recommendations, bookings, feedback, profile tips and trend display.',
    grid: 3,
    fields: [
      { key: 'showAdditionalRecommendationsPage', label: 'Recommendations Page', type: 'toggle' },
      { key: 'showAdditionalBookingsPage', label: 'Bookings Page', type: 'toggle' },
      { key: 'feedbackFlag', label: 'Feedback', type: 'toggle' },
      { key: 'showOnlyAbnormal', label: 'Only Abnormal', type: 'toggle' },
      { key: 'clientSpecificTips', label: 'Client-Specific Tips', type: 'toggle' },
      { key: 'showProfileTips', label: 'Profile Tips', type: 'toggle' },
      { key: 'showCustomProfileNames', label: 'Custom Profile Names', type: 'toggle' },
      { key: 'showDescendingTrend', label: 'Descending Trend', type: 'toggle' },
    ],
  },

  /* 12 ── PDF & Print ──────────────────────────────────────────── */
  {
    id: 'pdfPrint',
    icon: FaFilePdf,
    title: 'PDF & Print',
    desc: 'PDF generation, colored print and VizApp output settings.',
    grid: 3,
    fields: [
      { key: 'generatePrintPdf', label: 'Generate Print PDF', type: 'toggle' },
      { key: 'showColoredPrintPdf', label: 'Colored Print PDF', type: 'toggle' },
      { key: 'generateVizapp', label: 'Generate VizApp', type: 'toggle' },
    ],
  },

  /* 13 ── Data Integration & Dispatch ──────────────────────────── */
  {
    id: 'dataIntegration',
    icon: FaCloudUploadAlt,
    title: 'Data Integration & Dispatch',
    desc: 'S3 upload, WhatsApp dispatch, LIS mapping and parameter ordering.',
    grid: 3,
    fields: [
      { key: 'uploadReportToS3', label: 'Upload to S3', type: 'toggle' },
      { key: 'sendS3Link', label: 'Send S3 Link', type: 'toggle' },
      { key: 'sendS3LinkInResponse', label: 'S3 Link in Response', type: 'toggle' },
      { key: 'waReportDispatch', label: 'WhatsApp Dispatch', type: 'toggle' },
      { key: 'useLisMapping', label: 'Use LIS Mapping', type: 'toggle' },
      { key: 'retainDuplicateObservations', label: 'Retain Duplicates', type: 'toggle' },
      { key: 'retainLatestObservation', label: 'Retain Latest', type: 'toggle' },
      { key: 'enableMappingConfig', label: 'Enable Mapping Config', type: 'toggle' },
      { key: 'enableParameterOrder', label: 'Parameter Order', type: 'toggle' },
      { key: 'enableProfileOrder', label: 'Profile Order', type: 'toggle' },
      { key: 'ignoreIdMapping', label: 'Ignore Mapping', type: 'toggle' },
      { key: 'customMappingIdentifier', label: 'Custom Mapping ID', type: 'text' },
    ],
  },

  /* 14 ── Asset Paths ──────────────────────────────────────────── */
  {
    id: 'assetPaths',
    icon: FaLink,
    title: 'Asset Paths',
    desc: 'Base URLs for report assets, SVGs and image resources.',
    grid: 2,
    fields: [
      { key: 'base64assets', label: 'Base64 Assets', type: 'text' },
      { key: 'base64Prefix', label: 'Base64 Prefix', type: 'text' },
      { key: 'assetPath', label: 'Asset Path', type: 'text' },
      { key: 'baseAssetPath', label: 'Base Asset Path', type: 'text' },
      { key: 'baseAssetNormalPath', label: 'Normal Asset Path', type: 'text' },
      { key: 'baseAssetAbnormalPath', label: 'Abnormal Asset Path', type: 'text' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  MAPPED_KEYS – all keys explicitly covered by SECTION_CONFIG.      */
/*  Computed once at module level (SECTION_CONFIG is a constant).     */
/*  Any API key NOT in this set (and not hidden) lands in Other Flags */
/* ------------------------------------------------------------------ */
const MAPPED_KEYS = new Set(
  SECTION_CONFIG.flatMap(s => s.fields.map(f => f.key))
);

/* ------------------------------------------------------------------ */
/*  FieldRenderer – renders one field based on its type               */
/* ------------------------------------------------------------------ */
const FieldRenderer = ({ field, value, onChange }) => {
  if (field.type === 'toggle') {
    const isActive = value === true;
    return (
      <div
        className={`rc-toggle-card ${isActive ? 'active' : ''}`}
        onClick={() => onChange(!isActive)}
        tabIndex={0}
        role="button"
        aria-pressed={isActive}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!isActive); }
        }}
      >
        <span className="rc-toggle-label">{field.label}</span>
        <div className={`rc-toggle-switch ${isActive ? 'on' : 'off'}`}>
          <div className="rc-toggle-handle" />
        </div>
      </div>
    );
  }

  if (field.type === 'color') {
    const safeValue = value || '#000000';
    return (
      <div className="rc-form-group">
        <label className="rc-label">{field.label}</label>
        <div className="rc-input-wrapper rc-color-group">
          <input type="color" className="rc-color-picker" value={safeValue} onChange={e => onChange(e.target.value)} />
          <input className="rc-input rc-input-color-text" value={safeValue} onChange={e => onChange(e.target.value)} placeholder="#000000" />
        </div>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="rc-form-group">
        <label className="rc-label">{field.label}</label>
        <div className="rc-select-wrapper">
          <select
            className="rc-select"
            value={value ?? ''}
            disabled={field.disabled}
            onChange={e => onChange(e.target.value)}
          >
            <option hidden value="">Select an Option</option>
            {(field.options || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (field.type === 'textarea') {
    const display = typeof value === 'object' ? JSON.stringify(value, null, 2) : (value ?? '');
    return (
      <div className="rc-form-group">
        <label className="rc-label">{field.label}</label>
        <textarea
          className="rc-input rc-textarea"
          value={display}
          disabled={field.disabled}
          onChange={e => onChange(e.target.value)}
          rows={3}
        />
      </div>
    );
  }

  // text / number
  return (
    <div className="rc-form-group">
      <label className="rc-label">{field.label}</label>
      <input
        className="rc-input"
        type={field.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        disabled={field.disabled}
        onChange={e => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
const hasField = (config, key) => config != null && config[key] !== undefined;

/* ------------------------------------------------------------------ */
/*  VALUE-TYPE OPTIONS for the Add Custom Flag modal                  */
/* ------------------------------------------------------------------ */
const VALUE_TYPES = [
  { id: 'toggle', label: 'Toggle' },
  { id: 'text', label: 'Text' },
  { id: 'number', label: 'Number' },
];

/* ------------------------------------------------------------------ */
/*  AddCustomFlagModal – controlled modal for adding a new flag        */
/* ------------------------------------------------------------------ */
const AddCustomFlagModal = ({
  open, onClose, existingKeys, onAdd,
}) => {
  const [flagKey, setFlagKey] = useState('');
  const [valueType, setValueType] = useState('toggle');
  const [boolValue, setBoolValue] = useState(true);
  const [textValue, setTextValue] = useState('');
  const [numberValue, setNumberValue] = useState(0);
  const [keyError, setKeyError] = useState('');

  // reset form every time the modal opens
  useEffect(() => {
    if (open) {
      setFlagKey('');
      setValueType('toggle');
      setBoolValue(true);
      setTextValue('');
      setNumberValue(0);
      setKeyError('');
    }
  }, [open]);

  if (!open) return null;

  const validateKey = (raw) => {
    const k = (raw || '').trim();
    if (!k) return 'Flag key is required.';
    if (/\s/.test(k)) return 'Key cannot contain spaces.';
    if (existingKeys.has(k)) return `"${k}" already exists in the configuration.`;
    return '';
  };

  const handleAdd = () => {
    const err = validateKey(flagKey);
    if (err) { setKeyError(err); return; }

    let resolvedValue;
    if (valueType === 'toggle') resolvedValue = boolValue;
    else if (valueType === 'number') resolvedValue = Number(numberValue);
    else resolvedValue = textValue;

    onAdd({ key: flagKey.trim(), value: resolvedValue });
  };

  return (
    <div className="rcm-overlay" onClick={onClose}>
      <div className="rcm-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rcm-header">
          <div className="rcm-header-title">
            <FaPlus className="rcm-header-icon" />
            <span>Add Custom Flag</span>
          </div>
          <button className="rcm-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Note Banner */}
        <div className="rcm-note">
          <FaInfoCircle className="rcm-note-icon" />
          <p>
            Use this to manually add flags that are <strong>not yet provided by the API</strong> for
            your account. The key must exactly match the flag name expected by the report engine
            (case-sensitive). These flags will be merged into your configuration on the next
            <strong> Publish</strong>.
          </p>
        </div>

        {/* Flag Key */}
        <div className="rcm-field">
          <label className="rcm-label">Flag Key</label>
          <input
            className={`rcm-input${keyError ? ' rcm-input-error' : ''}`}
            type="text"
            placeholder="e.g. showNewFeature"
            value={flagKey}
            onChange={e => { setFlagKey(e.target.value); setKeyError(''); }}
            autoFocus
          />
          {keyError && <span className="rcm-error-msg">{keyError}</span>}
        </div>

        {/* Value Type Selector */}
        <div className="rcm-field">
          <label className="rcm-label">Value Type</label>
          <div className="rcm-type-group">
            {VALUE_TYPES.map(vt => (
              <button
                key={vt.id}
                type="button"
                className={`rcm-type-btn${valueType === vt.id ? ' active' : ''}`}
                onClick={() => setValueType(vt.id)}
              >
                {vt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Value Input — changes based on value type */}
        <div className="rcm-field">
          <label className="rcm-label">Value</label>
          {valueType === 'toggle' && (
            <div
              className={`rc-toggle-card${boolValue ? ' active' : ''}`}
              style={{ maxWidth: 260 }}
              onClick={() => setBoolValue(v => !v)}
              tabIndex={0}
              role="button"
              aria-pressed={boolValue}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBoolValue(v => !v); } }}
            >
              <span className="rc-toggle-label">{boolValue ? 'Enabled (true)' : 'Disabled (false)'}</span>
              <div className={`rc-toggle-switch${boolValue ? ' on' : ''}`}>
                <div className="rc-toggle-handle" />
              </div>
            </div>
          )}
          {valueType === 'text' && (
            <input
              className="rcm-input"
              type="text"
              placeholder="Enter text value"
              value={textValue}
              onChange={e => setTextValue(e.target.value)}
            />
          )}
          {valueType === 'number' && (
            <input
              className="rcm-input"
              type="number"
              value={numberValue}
              onChange={e => setNumberValue(e.target.value)}
            />
          )}
        </div>

        {/* Actions */}
        <div className="rcm-actions">
          <button type="button" className="rcm-btn rcm-btn-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="rcm-btn rcm-btn-add" onClick={handleAdd}>
            <FaPlus style={{ fontSize: '0.8rem' }} /> Add Flag
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  CustomFlagsSection – displays all user-added custom flags          */
/* ------------------------------------------------------------------ */
const CustomFlagsSection = ({ flags, onChange, onRemove }) => {
  if (!flags || flags.length === 0) return null;
  return (
    <div className="rc-section rc-custom-flags-section">
      <div className="rc-section-header">
        <div className="rc-section-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
          <FaCog />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2 className="rc-section-title" style={{ margin: 0 }}>Custom Flags</h2>
            <span className="rcf-badge">User-Added</span>
          </div>
        </div>
      </div>
      <p className="rc-section-desc">
        These flags were added manually and will be merged into your configuration on the next
        <strong> Publish</strong>. Review each flag before publishing.
      </p>
      <div className="rc-grid-3">
        {flags.map(flag => (
          <div key={flag.key} className="rcf-card">
            <div className="rcf-card-header">
              <span className="rcf-key-badge">{flag.key}</span>
              <button
                type="button"
                className="rcf-remove-btn"
                onClick={() => onRemove(flag.key)}
                title={`Remove ${flag.key}`}
                aria-label={`Remove flag ${flag.key}`}
              >
                <FaTrash />
              </button>
            </div>
            <div className="rcf-card-value">
              {typeof flag.value === 'boolean' ? (
                <div
                  className={`rc-toggle-card${flag.value ? ' active' : ''}`}
                  onClick={() => onChange(flag.key, !flag.value)}
                  tabIndex={0}
                  role="button"
                  aria-pressed={flag.value}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(flag.key, !flag.value); } }}
                >
                  <span className="rc-toggle-label">{flag.value ? 'Enabled (true)' : 'Disabled (false)'}</span>
                  <div className={`rc-toggle-switch${flag.value ? ' on' : ''}`}>
                    <div className="rc-toggle-handle" />
                  </div>
                </div>
              ) : (
                <input
                  className="rc-input"
                  type={typeof flag.value === 'number' ? 'number' : 'text'}
                  value={flag.value}
                  onChange={e => onChange(
                    flag.key,
                    typeof flag.value === 'number' ? Number(e.target.value) : e.target.value
                  )}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  inferType – determine the display widget for an unmapped value     */
/* ------------------------------------------------------------------ */
const inferType = (value) => {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return 'link';
    return 'string';
  }
  return 'unknown';
};

/* ------------------------------------------------------------------ */
/*  UnmappedFlagCard – renders one auto-detected flag with type chip   */
/* ------------------------------------------------------------------ */
const UnmappedFlagCard = ({ flagKey, value, onChange }) => {
  const type = inferType(value);

  const chipClass = {
    boolean: 'rcf-type-chip boolean',
    number: 'rcf-type-chip number',
    link: 'rcf-type-chip link',
    string: 'rcf-type-chip string',
  }[type] || 'rcf-type-chip string';

  const chipLabel = {
    boolean: 'Boolean',
    number: 'Number',
    link: 'Link',
    string: 'String',
  }[type] || 'Value';

  return (
    <div className="rcf-card rcf-unmapped-card">
      {/* Card header: key badge + type chip */}
      <div className="rcf-card-header">
        <span className="rcf-key-badge" title={flagKey}>{flagKey}</span>
        <span className={chipClass}>{chipLabel}</span>
      </div>

      {/* Value editor based on inferred type */}
      <div className="rcf-card-value">
        {type === 'boolean' && (
          <div
            className={`rc-toggle-card${value ? ' active' : ''}`}
            onClick={() => onChange(!value)}
            tabIndex={0}
            role="button"
            aria-pressed={value}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!value); } }}
          >
            <span className="rc-toggle-label">{value ? 'Enabled (true)' : 'Disabled (false)'}</span>
            <div className={`rc-toggle-switch${value ? ' on' : ''}`}>
              <div className="rc-toggle-handle" />
            </div>
          </div>
        )}

        {type === 'number' && (
          <input
            className="rc-input"
            type="number"
            value={value}
            onChange={e => onChange(Number(e.target.value))}
          />
        )}

        {type === 'link' && (
          <div className="rcf-link-value">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="rcf-link-anchor"
              title={value}
            >
              <FaExternalLinkAlt style={{ fontSize: '0.7rem', marginRight: '4px' }} />
              {value.length > 60 ? value.substring(0, 60) + '…' : value}
            </a>
            <input
              className="rc-input"
              type="text"
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder="Enter URL"
            />
          </div>
        )}

        {type === 'string' && (
          <input
            className="rc-input"
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
          />
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  UnmappedFlagsSection – auto-detected flags not in SECTION_CONFIG   */
/* ------------------------------------------------------------------ */
const UnmappedFlagsSection = ({ flags, onChange }) => {
  if (!flags || flags.length === 0) return null;

  const booleans = flags.filter(f => inferType(f.value) === 'boolean');
  const numbers = flags.filter(f => inferType(f.value) === 'number');
  const links = flags.filter(f => inferType(f.value) === 'link');
  const strings = flags.filter(f => inferType(f.value) === 'string');

  // Render order: booleans → numbers → strings → links
  const ordered = [...booleans, ...numbers, ...strings, ...links];

  return (
    <div className="rc-section rc-unmapped-flags-section">
      {/* Section header */}
      <div className="rc-section-header">
        <div className="rc-section-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
          <FaExclamationTriangle />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 className="rc-section-title" style={{ margin: 0 }}>Other Flags</h2>
            <span className="rcf-badge rcf-badge-unmapped">API · Unmapped · {flags.length}</span>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div className="rc-unmapped-info">
        <FaInfoCircle style={{ flexShrink: 0, marginTop: '2px', fontSize: '1rem' }} />
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          These <strong>{flags.length} flag{flags.length !== 1 ? 's' : ''}</strong> were returned by the API but
          are <strong>not yet mapped</strong> to any configuration section above.
          They are displayed here automatically so nothing gets hidden.
          You can <strong>edit them directly</strong> — changes will be included in your next{' '}
          <strong>Publish</strong>. Once a flag is added to a section in the code, it will
          move there automatically and disappear from this list.
        </p>
      </div>

      {/* Flag cards grid */}
      <div className="rc-grid-3">
        {ordered.map(flag => (
          <UnmappedFlagCard
            key={flag.key}
            flagKey={flag.key}
            value={flag.value}
            onChange={val => onChange(flag.key, val)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Extracts stateData from API response. Handles multiple possible structures:
 * - response.data.data.stateData.stateData (API double-nests stateData)
 * - response.data.data.stateData (single nest)
 * - response.data.stateData (alternate)
 * Returns {} if structure is unexpected.
 */
const extractStateDataFromResponse = (response) => {
  if (!response?.data) return {};
  const d = response.data;
  const inner = d?.data?.stateData;
  if (inner != null) {
    // API returns { data: { stateData: { stateData: {...actual config} } } }
    if (inner?.stateData != null && typeof inner.stateData === 'object' && !Array.isArray(inner.stateData)) {
      return inner.stateData;
    }
    return inner;
  }
  if (d?.stateData != null) return d.stateData;
  if (d?.data != null && typeof d.data === 'object' && !Array.isArray(d.data)) return d.data;
  return {};
};

/* ------------------------------------------------------------------ */
/*  ReportConfiguration component                                     */
/* ------------------------------------------------------------------ */
const ReportConfiguration = () => {
  const [saveOption, setSaveOption] = useState(false);
  const { refreshAccessConfig } = useAccessConfig();

  const [stateConfiguration, setStateConfiguration] = useState({});

  const [loading, setLoading] = useState(false);
  const [updatingStateLoading, setUpdatingStateLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  /* ---------- Custom Flags state (additive — no existing state touched) ---------- */
  const [customFlags, setCustomFlags] = useState([]);
  const [showAddFlagModal, setShowAddFlagModal] = useState(false);

  const getStateConfiguration = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'GET',
        url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    const existingStateConfigBody = {
      headers: { authorization: idToken },
      method: 'GET',
      url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
      validateStatus: function (status) { return status >= 200 && status <= 401; },
    };
    const existingStateConfigResponse = await axios(existingStateConfigBody);
    return existingStateConfigResponse;
  };

  const updateStateConfiguration = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'POST',
        url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
        data: stateConfiguration,
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    const stateConfigurationResponseConfig = {
      headers: { authorization: idToken },
      method: 'POST',
      url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
      data: JSON.stringify(stateConfiguration),
      validateStatus: function (status) { return status >= 200 && status <= 401; },
    };
    const stateConfigurationResponse = await axios(stateConfigurationResponseConfig);
    return stateConfigurationResponse;
  };

  useEffect(() => {
    (async function RetrieveExistingColorConfiguration() {
      setLoading(true);
      try {
        let existingStateConfigResponse = await getStateConfiguration();
        if (existingStateConfigResponse?.status === 401) {
          await regenerateToken();
          existingStateConfigResponse = await getStateConfiguration();
        }
        const stateData = extractStateDataFromResponse(existingStateConfigResponse);
        // Ensure ignoreIdMapping defaults to false if not present
        if (stateData.ignoreIdMapping === undefined) {
          stateData.ignoreIdMapping = false;
        }
        // Ensure Prenatal fields are present so the section always shows.
        // Toggles default to false, text fields default to empty.
        const PRENATAL_DEFAULTS = {
          prenatal: false,
          generatePrenatalCoverPage: false,
          prenatalCoverPageImage: '',
          generatePrenatalBackPage: false,
          prenatalBackPageImage: '',
          prenatalHeaderBase64: '',
          prenatalFooterBase64: '',
          prenatalHeaderHeight: '',
          prenatalFooterHeight: '',
          prenatalHeaderMargin: '',
          prenatalFooterMargin: '',
          showPoweredByLogo: false,
        };
        Object.entries(PRENATAL_DEFAULTS).forEach(([key, def]) => {
          if (stateData[key] === undefined) {
            stateData[key] = def;
          }
        });
        setStateConfiguration(stateData);
      } catch (error) {
        console.error('[ReportConfiguration] Failed to fetch configuration:', error?.response?.data || error?.message || error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlingUpdate = (key, value) => {
    setSaveOption(true);
    setStateConfiguration(prev => ({ ...prev, [key]: value }));
  };

  /* ---------- Custom flag handlers ---------- */
  const handleAddCustomFlag = ({ key, value }) => {
    setCustomFlags(prev => [...prev, { key, value }]);
    setShowAddFlagModal(false);
    setSaveOption(true);
  };

  const handleRemoveCustomFlag = (key) => {
    setCustomFlags(prev => prev.filter(f => f.key !== key));
    setSaveOption(true);
  };

  const handleCustomFlagValueChange = (key, value) => {
    setCustomFlags(prev => prev.map(f => f.key === key ? { ...f, value } : f));
    setSaveOption(true);
  };

  /*
   * Build the full set of keys that already exist (API flags + user-added flags)
   * so the modal can block duplicates.
   */
  const allExistingFlagKeys = new Set([
    ...Object.keys(stateConfiguration),
    ...customFlags.map(f => f.key),
  ]);

  /* ---------- Unmapped flags: API keys not in SECTION_CONFIG or HIDDEN_FIELDS ---------- */
  const unmappedFlags = useMemo(() =>
    Object.entries(stateConfiguration)
      .filter(([key, value]) =>
        !MAPPED_KEYS.has(key) &&
        !HIDDEN_FIELDS.has(key) &&
        value !== null &&
        value !== undefined &&
        typeof value !== 'object'
      )
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => a.key.localeCompare(b.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stateConfiguration]);


  const handlingRequest = async () => {
    /*
     * MERGE STRATEGY: custom flags are merged on top of the API-sourced
     * stateConfiguration. The merged object is what gets posted.
     * Existing updateStateConfiguration() still uses stateConfiguration from
     * state, so we temporarily build the merged snapshot for the POST body
     * inline here — we do NOT mutate stateConfiguration.
     */
    const customFlagsObj = Object.fromEntries(customFlags.map(f => [f.key, f.value]));
    const mergedState = { ...stateConfiguration, ...customFlagsObj };
    const requestBody = { stateData: mergedState };
    console.log('requestBody', requestBody);

    setUpdatingStateLoading(true);
    try {
      // ── ADMIN PATH: use admin token + inject clientId if a client is selected
      let stateConfigurationResponse;
      if (checkAdmin()) {
        stateConfigurationResponse = await axios(buildRequestConfig({
          method: 'POST',
          url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
          data: mergedState,
        }));
        if (stateConfigurationResponse.status === 401) {
          await regenerateToken();
          stateConfigurationResponse = await axios(buildRequestConfig({
            method: 'POST',
            url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
            data: mergedState,
          }));
        }
      } else {
        // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
        const idToken = Cookies.get('idToken');
        const postConfig = {
          headers: { authorization: idToken },
          method: 'POST',
          url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/state',
          data: JSON.stringify(mergedState),
          validateStatus: (status) => status >= 200 && status <= 401,
        };
        stateConfigurationResponse = await axios(postConfig);
        if (stateConfigurationResponse.status === 401) {
          console.log('Regenerating ID Token');
          await regenerateToken();
          const idToken2 = Cookies.get('idToken');
          stateConfigurationResponse = await axios({ ...postConfig, headers: { authorization: idToken2 } });
        }
      }
      setUpdatingStateLoading(false);
      console.log(
        stateConfigurationResponse.data.data.acknowledged
          ? 'Successfully Updated the State configuration'
          : 'Not Updated Successfully'
      );
      if (stateConfigurationResponse.data.data.acknowledged) {
        /*
         * Promote custom flags into stateConfiguration so the page
         * reflects the newly published values without a full reload.
         */
        setStateConfiguration(prev => ({ ...prev, ...customFlagsObj }));
        setCustomFlags([]);
        setSaveOption(false);
        await refreshAccessConfig();
      }
    } catch (e) {
      console.log('Error in updating report Configuration', e.message);
      setUpdatingStateLoading(false);
    }
  };

  /*
   * Visibility logic (two layers):
   * 1. HIDDEN_FIELDS → permanently excluded from the UI
   * 2. hasField()    → only show fields that exist in the API response
   * If every field in a section is hidden/absent, the entire section card is hidden.
   */
  const visibleSections = SECTION_CONFIG
    .map(section => ({
      ...section,
      fields: section.fields.filter(
        f => !HIDDEN_FIELDS.has(f.key) && hasField(stateConfiguration, f.key)
      ),
    }))
    .filter(section => section.fields.length > 0);

  /* Search: filter sections and fields by query (label or key), case-insensitive */
  const searchTrimmed = (searchQuery || '').trim().toLowerCase();
  const searchFilteredSections =
    searchTrimmed === ''
      ? visibleSections
      : visibleSections
        .map(section => ({
          ...section,
          fields: section.fields.filter(
            f =>
              (f.label && f.label.toLowerCase().includes(searchTrimmed)) ||
              (f.key && f.key.toLowerCase().includes(searchTrimmed))
          ),
        }))
        .filter(section => section.fields.length > 0);

  /* Filter unmapped flags by search query (key name only) */
  const searchFilteredUnmappedFlags =
    searchTrimmed === ''
      ? unmappedFlags
      : unmappedFlags.filter(f =>
        f.key.toLowerCase().includes(searchTrimmed)
      );

  const hasSearchActive = searchTrimmed !== '';
  const totalSearchMatches =
    searchFilteredSections.reduce((acc, s) => acc + s.fields.length, 0) +
    searchFilteredUnmappedFlags.length;
  const hasNoSearchResults = hasSearchActive && totalSearchMatches === 0;

  return (
    <AppLayout>
      {loading ? (
        <Loader />
      ) : (
        <div className="rc-container">
          <div className="rc-header">
            <div className="rc-header-top">
              <div>
                <h1 className="rc-title">Report Configuration</h1>
                <p className="rc-subtitle">
                  Manage settings, feature flags, and visual styles for your reports.
                  Only fields available for your account are shown below.
                </p>
              </div>
              <button
                type="button"
                className="rcm-add-flag-btn"
                onClick={() => setShowAddFlagModal(true)}
                title="Add a custom flag that is not in your current configuration"
              >
                <FaPlus style={{ fontSize: '0.85rem' }} />
                Add Custom Flag
              </button>
            </div>
          </div>

          {/* Search flags */}
          {visibleSections.length > 0 && (
            <div className="rc-search-wrap">
              <FaSearch className="rc-search-icon" aria-hidden />
              <input
                type="text"
                className="rc-search-input"
                placeholder="Search flags by name or key..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                aria-label="Search configuration flags"
              />
              {hasSearchActive && (
                <button
                  type="button"
                  className="rc-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  title="Clear search"
                >
                  <FaTimes />
                </button>
              )}
              {hasSearchActive && (
                <span className="rc-search-hint">
                  {totalSearchMatches === 0
                    ? 'No matches'
                    : `${totalSearchMatches} match${totalSearchMatches !== 1 ? 'es' : ''}`}
                </span>
              )}
            </div>
          )}

          {searchFilteredSections.map(section => {
            const IconComponent = section.icon;
            return (
              <div className="rc-section" key={section.id}>
                <div className="rc-section-header">
                  <div className="rc-section-icon"><IconComponent /></div>
                  <h2 className="rc-section-title">{section.title}</h2>
                </div>
                <p className="rc-section-desc">{section.desc}</p>
                <div className={`rc-grid-${section.grid || 3}`}>
                  {section.fields.map(field => (
                    <FieldRenderer
                      key={field.key}
                      field={field}
                      value={stateConfiguration[field.key]}
                      onChange={(val) => handlingUpdate(field.key, val)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {hasNoSearchResults && (
            <div className="rc-empty-state rc-search-no-results">
              <FaSearch style={{ fontSize: '2.5rem', color: '#cbd5e1', marginBottom: '1rem' }} />
              <p>No flags match &quot;{searchQuery.trim()}&quot;.</p>
              <button type="button" className="rc-btn rc-search-clear-btn" onClick={() => setSearchQuery('')}>
                Clear search
              </button>
            </div>
          )}

          {visibleSections.length === 0 && !hasSearchActive && (
            <div className="rc-empty-state">
              <FaCog style={{ fontSize: '2.5rem', color: '#cbd5e1', marginBottom: '1rem' }} />
              <p>No configuration data available for this account.</p>
            </div>
          )}

          {/* ── Other Flags Section: auto-detected unmapped API flags ── */}
          <UnmappedFlagsSection
            flags={hasSearchActive ? searchFilteredUnmappedFlags : unmappedFlags}
            onChange={handlingUpdate}
          />

          {/* ── Custom Flags Section (only rendered when flags exist) ── */}
          {!hasSearchActive && (
            <CustomFlagsSection
              flags={customFlags}
              onChange={handleCustomFlagValueChange}
              onRemove={handleRemoveCustomFlag}
            />
          )}

          {/* ── Add Custom Flag Modal ── */}
          <AddCustomFlagModal
            open={showAddFlagModal}
            onClose={() => setShowAddFlagModal(false)}
            existingKeys={allExistingFlagKeys}
            onAdd={handleAddCustomFlag}
          />

          {/* Floating Action Bar */}
          <div className="rc-actions">
            <div className="rc-actions-status">
              {saveOption ? (
                <span className="rc-status-unsaved">Unsaved Changes</span>
              ) : (
                <span className="rc-status-saved"><FaCheckCircle style={{ marginRight: '6px' }} /> All Changes Saved</span>
              )}
            </div>
            <div className="rc-actions-buttons">
              <button
                className="rc-btn rc-btn-primary"
                onClick={() => handlingRequest()}
                disabled={!saveOption}
              >
                {updatingStateLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>Publish Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ReportConfiguration;
